import QueueLengthChartTooltip from "./queue-length-chart-tooltip";
import useInteractionsCounts from "hooks-for/interactions/use-interactions-counts";
import { Box, BoxContent, BoxHeader, BoxHeading, BoxSubheading } from "@edgetier/client-components";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { format } from "date-fns";
import { formatNumericTickValue } from "utilities/format-numeric-tick-value";
import { FunctionComponent, memo, useMemo, useState } from "react";
import { createXAxisTicks, getDateTimes, getMillisecondsUntilNextPoll } from "./queue-length-chart.utilities";
import { IInteractionsCount, TimePeriodGroupBy } from "@edgetier/watchtower-types";
import { IProps } from "./queue-length-chart.types";
import { SpinnerUntil } from "@edgetier/components";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { useTimeoutFn } from "react-use";
import "./queue-length-chart.scss";

/**
 * Chart the recent newly created interactions over time.
 * @returns Line chart.
 */
const QueueLengthChart: FunctionComponent<IProps> = () => {
    const [dateTimeBoundaries, setDateTimeBoundaries] = useState(getDateTimes);
    const interactionQueryParameters = useInteractionQueryParameters();

    const queryParameters = {
        ...interactionQueryParameters,
        ...dateTimeBoundaries,
        groupBy: TimePeriodGroupBy.Minutes,
    };
    const { data, isLoading } = useInteractionsCounts<IInteractionsCount<Date>[]>(queryParameters, {
        select: (counts) => counts.filter((count) => count.group instanceof Date),
    });

    const xAxisticks = useMemo(() => createXAxisTicks(data ?? []), [data]);

    // Update the start and end date every minute.
    useTimeoutFn(() => {
        setDateTimeBoundaries(getDateTimes());
    }, getMillisecondsUntilNextPoll());

    return (
        <Box className="queue-length-chart">
            <BoxHeader>
                <BoxHeading>New Interactions</BoxHeading>
                <BoxSubheading>Number of customers joining the queue per minute</BoxSubheading>
            </BoxHeader>
            <BoxContent>
                <SpinnerUntil<IInteractionsCount<Date>[]>
                    data={data}
                    isReady={!isLoading && typeof data !== "undefined"}
                >
                    {(chartData) => (
                        <ResponsiveContainer height="100%" width="100%">
                            <LineChart data={chartData}>
                                <XAxis
                                    dataKey="group"
                                    domain={[
                                        chartData[0].group.getTime(),
                                        chartData[chartData.length - 1].group.getTime(),
                                    ]}
                                    tickFormatter={(value) => format(value, "HH:mm")}
                                    ticks={xAxisticks}
                                    type="number"
                                />
                                <YAxis tickFormatter={formatNumericTickValue} />
                                <CartesianGrid />
                                <Line
                                    dataKey="count"
                                    dot={false}
                                    name="Queue"
                                    stroke="#e74c3c"
                                    strokeWidth="2"
                                    type="monotoneX"
                                />
                                <Tooltip
                                    content={({ active, payload }) =>
                                        active &&
                                        Array.isArray(payload) &&
                                        typeof payload[0].value === "number" && (
                                            <QueueLengthChartTooltip
                                                count={payload[0].value}
                                                dateTime={payload[0].payload.group}
                                            />
                                        )
                                    }
                                    isAnimationActive={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </SpinnerUntil>
            </BoxContent>
        </Box>
    );
};

export default memo(QueueLengthChart);
